import {Composition} from 'APIs/letrusAPI/composition/types';
import {fromJS} from 'immutable';
import {useLocation, useNavigate} from 'react-router-dom';

import {CompositionDetailExtended} from 'utils/types/compositions';
import {Activity} from 'APIs/letrusAPI/activities/types';

export interface UseCompositionNavegationParams {
  composition?: ImmutableMap<CompositionDetailExtended>;
  activity?: Activity;
}

interface UseCompositionNavegationReturn {
  redirectCompositionByStatus(compositionResponse?: Composition): void;
  toggleWriteToReWrite(state?: Record<string, unknown>): void;
}

const FUVEST_REVIEW_GRID_ID = 2;
const MULTIGENRE_REVIEW_GRID_ID = 61;

function useCompositionNavegation({
  composition,
  activity,
}: UseCompositionNavegationParams): UseCompositionNavegationReturn {
  const navigate = useNavigate();
  const location = useLocation();

  const reviewGrid = composition?.getIn([
    'instruction_id',
    'genre',
    'review_grid',
  ]);

  const {pathname, state: historyState} = location;
  const hasCompositionReference = composition?.size;
  const isFuvest = reviewGrid === FUVEST_REVIEW_GRID_ID;
  const isMultigenreRewriting = historyState?.hasRewriting;
  const isMultigenre = reviewGrid === MULTIGENRE_REVIEW_GRID_ID;

  function getSubRouteByStatus(status = '', hasWritePreparation: boolean) {
    let reviewRoute = '/nova_devolutiva';

    if (composition?.getIn(['test', 'uses_ai_comp'])) {
      reviewRoute = isMultigenre ? '/devolutiva' : '/corrigida';
    }

    const RoutesByStatus = {
      writing: '/',
      preparing: hasWritePreparation ? '/preparacao' : '/',
      finished: isFuvest ? '/' : reviewRoute,
      reviewed: isFuvest ? '/corrigida' : reviewRoute,
      'manual review pending': isFuvest ? '/' : reviewRoute,
      'automatic review running': isFuvest ? '/' : reviewRoute,
      'teacher revision pending': isFuvest ? '/' : reviewRoute,
    };

    return RoutesByStatus[status];
  }

  function getRouteFromComposition(
    composition?: ImmutableMap<CompositionDetailExtended>,
  ) {
    const statusComposition = composition?.get('status');
    const hasWritePreparation = !!composition?.get('uses_write_preparation');

    return getSubRouteByStatus(statusComposition, hasWritePreparation);
  }

  function getRouteFromActivity(activity: Activity) {
    const activityStatus = activity.composition_status ?? activity.status;
    const hasWritePreparation = !!activity.uses_write_preparation;

    return getSubRouteByStatus(activityStatus, hasWritePreparation);
  }

  function redirectToRoute(route: string, id: string | number) {
    if (!pathname.includes(route) && (composition?.size || activity)) {
      navigate(
        {
          pathname: `/atividades/escrita/${id}${route}`,
          search: location.search,
        },
        {replace: true},
      );
    }
  }

  function redirectCompositionByStatus(compositionResponse?: Composition) {
    const compositionId =
      compositionResponse?.id ??
      composition?.get('id') ??
      activity?.composition_id;

    const route = hasCompositionReference
      ? getRouteFromComposition(
          compositionResponse ? fromJS(compositionResponse) : composition,
        )
      : activity && getRouteFromActivity(activity);

    if (compositionId && !isMultigenreRewriting) {
      redirectToRoute(route, compositionId);
    }
  }

  function toggleWriteToReWrite(state?: Record<string, unknown>) {
    const parentId = composition?.get('parent_id');
    const childId = composition?.get('child_id');
    const targetCompositionId = parentId || childId;

    if (targetCompositionId) {
      navigate(
        {
          pathname: `/atividades/escrita/${targetCompositionId}`,
          search: location.search,
        },
        {state},
      );
    }
  }

  return {redirectCompositionByStatus, toggleWriteToReWrite};
}

export default useCompositionNavegation;
